<template>
    <div>
        <v-data-table
            :dense="dense"
            :headers="headers"
            :items="customers"
            :item-class="customerValidator"
            :item-key="customerKey"
            :loading="loading"
            :disable-pagination="!paginated"
            :hide-default-footer="!paginated"
            mobile-breakpoint="900"
            loading-text="Loading... Please wait">

            <template v-slot:[`item.name`]="{ item }">
                {{ item.name }} ({{ item.email }})
            </template>

            <template v-slot:[`item.report`]="{ item }">
                <ReportControls :customer="item" @updated="report"/>
            </template>
        
            <template v-slot:[`item.actions`]="{ item }">
                <v-icon small class="mr-2" @click="edit(item)">mdi-pencil</v-icon>
                <v-icon small @click="toRemove = item">mdi-delete</v-icon>
            </template>
        </v-data-table>

        <v-dialog v-model="confirmation" persistent max-width="290" :retain-focus="false">
            <v-card>
                <v-card-title>Remove confirmation</v-card-title>
                <v-card-text>Are you sure you want to delete <b>{{ confirmation ? toRemove.name : '' }}</b>?</v-card-text>

                <v-card-actions>
                    <v-btn text @click="confirmRemove(toRemove)">Confirm</v-btn>
                    <v-btn text @click="toRemove = null">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialog" width="500">
            <v-card>
                <v-card-title>
                    <v-icon left>mdi-pencil</v-icon>
                    {{ draftCustomer.name }}
                </v-card-title>
                <v-divider/>
                <v-card-text style="max-height: 600px;">
                    <CustomerForm :customer="draftCustomer" @submit="update" :newCustomer="emailEditable"/>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import ReportControls from '@/components/customers/ReportControls';
import CustomerForm from '@/components/customers/CustomerForm';

/**
 * CustomerList is a visual component that allows to display a list of 
 * customers. It also allows to fire actions over each customer that must be 
 * listened by parent component to complete the fired action.
 */
export default {
    name: 'CustomersList',
    props: {
        customerKey: {
            type: String,
            required: true
        },
        customers: {
            type: Array,
            required: true
        },
        dense: {
            type: Boolean,
            default: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        emailEditable: {
            type: Boolean,
            default: true
        },
        paginated: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        confirmation() {
            return this.toRemove !== null;
        }
    },
    data: () => ({
        dialog: false,
        toRemove: null,
        draftIndex: -1,
        draftCustomer: {},
        headers: [
            { text: 'Customer', value: 'name' },
            { text: 'Clinics', value: 'clinics', width: '30%' },
            { text: 'Report', value: 'report' },
            { text: 'Actions', value: 'actions', sortable: false }
        ],
        reportFrecuencies: [
            { label: 'Daily', value: 'daily' },
            { label: 'Weekly', value: 'weekly' },
            { label: 'Monthly', value: 'monthly' },
            { label: 'Don´t send', value: false }
        ],
        customerValidator: (customer) => {
            const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

            const validName = !!customer.name;
            const validClinics = customer.clinics && customer.clinics.length > 0;
            const validEmail = emailPattern.test(customer.email);

            const valid = validName && validEmail && validClinics;
            return !valid ? 'red--text' : '';
        },
        rules: {
            required: value => !!value || 'Required.',
            requiredList: value => (!!value && value.length > 0) || 'Required.',
            email: value => {
                const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                return pattern.test(value) || 'Invalid e-mail.'
            }
        }
    }),
    methods: {
        edit(customer) {
            this.draftIndex = this.customers.indexOf(customer);
            this.draftCustomer = Object.assign({}, customer);
            this.dialog = true;
        },
        update(customer) {
            this.$emit('updated', this.draftIndex, customer);
            this.dialog = false;
        },
        report(customer) {
            this.draftIndex = this.customers.indexOf(customer);
            this.$emit('report', this.draftIndex, customer);
            this.dialog = false;
        },
        confirmRemove(customer) {
            this.toRemove = null;
            this.remove(customer);
        },
        remove(customer) {
            this.draftIndex = this.customers.indexOf(customer);
            this.$emit('removed', this.draftIndex, customer);
        }
    },
    components: { ReportControls, CustomerForm }
}
</script>